// src/belediye/pages/gorevler/GorevAtaPage.js
// FAZ-56 — "Görev Ata" (/yonetim/gorev-ata): GÖREVİ VERENİN sayfası.
//
// Kapsam SAYFADAN gelir (K-2): kullanıcı burada YALNIZ kendi verdiği görevleri
// görür — İSTİSNASIZ. 2026-07-28'de K-8 ("view_all varsa tümünü göster")
// KALDIRILDI: hem veren hem alan olan kullanıcıda (başkan yardımcısı, müdür)
// kendi üzerine verilmiş işleri "verdiklerim" listesine sızdırıyordu.
//
// 2026-07-28 kullanıcı kararları:
//   • "Yeni Görev" ÇİP (buton değil) ve sırası: arama → filtre → yeni görev → bilgi
//   • "Ne durumdayız?" artık çekmece değil, çipin yanında açılan POPOVER;
//     hazır soruya tıklamak soruyu DOĞRUDAN gönderir.
//   • Mobilde "Yeni Görev" alt köşede küçük ÇİP (yuvarlak FAB değil).
//
// FAZ-75 (2026-07-30) — GÖREV VERMENİN TEK YÜZEYİ SOHBET PENCERESİ.
// Kullanıcı: "Yeni göreve tıklayınca çıkan o form yapısını, liste sayfasından
// aşağı gelen yapıyı tamamıyla kaldır. Yeni görev deyince sohbetler kısmı
// 'Yeni'ye tıklanmış ve seçilmiş şekilde ORTA boyda açılsın; kişileri görsün,
// kişi seçtikten sonra 'Sohbeti Başlat' deyip görevi oradan oluştursun."
//
// Bu yüzden sayfa içi oluşturma paneli (QuickCreateForm) ve onun `olusturAcik`
// durumu KALDIRILDI. Sayfa artık yalnız LİSTEDİR; "Yeni Görev" çipi köprüye
// (openNewChat) dokunur, gerisi ChatDock'un işidir. Aynı eylemin iki yüzeyi
// olmasın kuralının bu sayfadaki karşılığı budur (bkz. FAZ-64: "detay sayfası
// kayıttır, kumanda sohbettedir").

import React, { useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { ChatBubbleOvalLeftEllipsisIcon, PlusIcon } from "@heroicons/react/24/outline";
import { toast } from "react-toastify";

import usePermissions from "../../hooks/usePermissions";
import useModuleEnabled from "../../hooks/useModuleEnabled";
// FAZ-90: telefonda "Yeni Talep" düğmesi yüzmez, araç satırında durur
import useKatman from "../../hooks/useKatman";
import GorevListeIskeleti from "../../components/tasks/GorevListeIskeleti";
import { OrtakFiltreler } from "../../components/tasks/GorevFiltreleri";
import { SoruPopover } from "../../components/tasks/GorevOnayPopover";
import { PRESET_QUESTIONS } from "../../components/tasks/GorevBits";
import { askStatus } from "../../services/internalTasksApi";
import { openNewChat } from "../../services/mesajlasmaBridge";
import "../../components/tasks/gorev.css";

// Bilgi metni: teknik/tasarım sözcüğü YOK ("çip", "sekme", "filtre paneli" vb.).
// Kullanıcının işini anlatır.
const BILGI = (
  <div className="gk-bilgi">
    <p className="gk-bilgi-giris">
      Burası <b>sizin verdiğiniz</b> işlerin sayfası. Kime ne verdiğinizi, hangi
      işin ne durumda olduğunu ve gecikeni buradan takip edersiniz.
    </p>

    <h4>Üstteki başlıklar ne anlatıyor?</h4>
    <p className="gk-bilgi-ipucu">
      Her iş <b>tek bir başlıkta</b> durur; başlıklar birbirini tekrar etmez.
      Beşinin toplamı verdiğiniz bütün işlere eşittir.
    </p>
    <dl className="gk-bilgi-liste">
      <dt data-t="yeni">Yeni</dt>
      <dd>
        Yolunda giden işler: yeni verdikleriniz, üzerinde çalışılanlar, teslim
        tarihi uzak ya da hiç tarih verilmemiş olanlar. Acele eden yok.
      </dd>
      <dt data-t="onay">Onay Bekleyen</dt>
      <dd>Sorumlu işi bitirip size teslim etti; <b>sıra sizde</b>. Girip onaylayın ya da düzeltme isteyin.</dd>
      <dt data-t="geciken">Geciken</dt>
      <dd>Teslim tarihi geçmiş, hâlâ bitmemiş işler.</dd>
      <dt data-t="yaklasan">Yakındakiler</dt>
      <dd>Teslim tarihi kapıya dayanmış işler. Hatırlatma yapmak için iyi bir yer.</dd>
      <dt data-t="tamamlanan">Tamamlanan</dt>
      <dd>
        Kapanmış işler — onayınızdan geçenler <i>ve</i> iptal ettikleriniz.
        Ne kadar geriye bakılacağını filtreden seçebilirsiniz.
      </dd>
    </dl>

    <h4>Sağdaki kutular</h4>
    <p>
      Hangi başlıkta olursanız olun, sağda <b>üç kutu</b> durur ve o an
      bakmadığınız işleri gösterir. Bulunduğunuz başlığın kutusu tekrar
      gösterilmez — zaten solda tam listesi açıktır. Bir kutunun altındaki{" "}
      <b>Tümünü gör</b>, sizi o başlığa götürür.
    </p>

    <h4>Neler yapabilirsiniz?</h4>
    <ul>
      <li><b>Arama</b> — işin konusuyla, talebi verdiğiniz kişinin ya da birimin adıyla arayın.</li>
      <li><b>Filtre</b> — teslim tarihi, ne zaman verildiği, aciliyet ve işin bir başvurudan doğup doğmadığı.</li>
      <li><b>Ne durumdayız?</b> — satırdaki bu düğme, sorumluya hazır bir soru gönderir; bildirimi anında alır.</li>
      <li><b>Bir işe tıklayın</b> — ayrıntısına girer, sorumluyla yazışır, teslim edileni onaylayıp kapatırsınız.</li>
    </ul>

    <p className="gk-bilgi-dip">
      Size <i>verilen</i> işler burada değil — onlar <b>Gelen Talepler</b> sayfasında.
    </p>
  </div>
);

// NOT: burada statü listesi ya da "ben mi oluşturdum" hesabı YOK. İkisi de
// backend'in `viewer` bloğundan gelir (serializers.TaskListSerializer) —
// nesne-düzeyi kuralın frontend'de ikinci bir kopyası tutulmaz.

export default function GorevAtaPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { can } = usePermissions();

  const tazeleRef = useRef(null);

  // Kapsam SAYFADAN gelir — izinden DEĞİL.
  //
  // KULLANICI KARARI (2026-07-28): "Gelen görevler Görevlerim sayfasında,
  // kişinin atadıkları Görev Ata'da olacak." Eski kural kapsamı izinden
  // türetiyordu (`view_all` varsa scope="all") ve HEM VEREN HEM ALAN olan
  // başkan yardımcısında sayfayı bozuyordu: Görevlerim'de 2 iş varken Görev
  // Ata'da 3 iş görünüyor, üçünün ikisi KENDİSİNE GELEN işler oluyordu.
  // scope="all" = Task.objects.all() olduğu için kendi üzerindeki işler de
  // "verdiklerim" listesine karışıyordu.
  //
  // Artık bu sayfa TEK bir küme demektir: created_by = ben. Süzme backend'de
  // (selectors.scoped_tasks_for "created") yapılır — veri hiç gelmez, gizlenmez.
  // Liste görme yetkisi yoksa (yalnız tasks.create olan kullanıcı — köprüden
  // gelir) kapsam null: sekme ve liste isteği YOK, sayfa yalnız görev vermeye yarar.
  const kapsam = can("tasks.view_created") ? "created" : null;
  // FAZ-75: görev verme yüzeyi SOHBET PENCERESİ olduğu için çip yalnız pencere
  // gerçekten açılabiliyorsa çizilir. Yoksa hiçbir şey yapmayan bir düğme kalırdı
  // (ChatDock `uiOn && moduleOn && mesajlasma.view` olmadan hiç render edilmez,
  // dolayısıyla köprü olayını dinleyen de olmaz).
  const sohbetVar = useModuleEnabled("mesajlasma") && can("mesajlasma.view");
  const olusturabilir = can("tasks.create") && sohbetVar;

  // Köprü ön-dolumu: ?application= / ?title= / ?kaynak_sohbet=
  const prefill = useMemo(() => {
    const appId = Number(searchParams.get("application")) || null;
    const title = searchParams.get("title") || "";
    const sourceConversationId = Number(searchParams.get("kaynak_sohbet")) || null;
    if (!appId && !title && !sourceConversationId) return null;
    return {
      application_id: appId,
      application_label: searchParams.get("tracking") || (appId ? `#${appId}` : ""),
      title,
      source_conversation_id: sourceConversationId,
    };
  }, [searchParams]);

  // KÖPRÜDEN GELİŞ (?application= / ?title= / ?kaynak_sohbet=).
  // Eskiden bu parametreler sayfa içi formu ön-dolu açıyordu. Form kalktığı için
  // artık aynı yere gidiyoruz: yeni sohbet paneli. Parametreler ANINDA
  // temizlenir, yoksa her render'da pencere yeniden açılır.
  //
  // BİLİNEN SINIR: başvuru bağı (application_id) bu yolla otomatik taşınmıyor —
  // görev sohbetten oluşuyor ve oradaki akış başvuru seçimini kendi soruyor.
  // Kullanıcıya bildirildi; istenirse köprü sohbet akışına parametre taşıyacak
  // şekilde genişletilir.
  useEffect(() => {
    if (!prefill || !olusturabilir) return;
    openNewChat("orta");
    setSearchParams({}, { replace: true });
  }, [prefill, olusturabilir, setSearchParams]);

  const soruGonder = async (task, soru) => {
    try {
      await askStatus(task.id, soru);
      toast.success("Soru iletildi — sorumluya bildirim gitti.");
    } catch {
      toast.error("Soru iletilemedi.");
    }
  };

  const filtreler = ({ filtreler: f, yamala, gorunum }) => (
    <OrtakFiltreler filtreler={f} yamala={yamala} cip={gorunum === "tamamlanan" ? "tamamlanan" : ""} />
  );

  // FAZ-58 — sağdaki dikkat şeridi. Üç slot: gecikenler → onay bekleyenler →
  // yakındakiler. Hangi sekmedeysen o slot "yeni"ye dönüşür (aynı iş ekranda
  // iki kez görünmesin). "Tümünü gör" pencere açmaz, ilgili SEKMEYE geçirir.
  const seritKartlari = ({ scope }) => {
    if (!scope) return null;
    return {
      gecikti: {
        key: "gecikti",
        baslik: "Gecikenler",
        tone: "gecikti",
        sekme: "geciken",
        bosMetin: "Geciken iş yok.",
        // En ÇOK geciken üstte (termin = en eski tarih önce).
        params: { scope, overdue: 1, sort: "termin" },
      },
      onayda: {
        key: "onayda",
        // Başlık sekmenin adıyla aynı aileden ("Onay Bekleyen") — kullanıcı
        // 2026-07-29: "onayınızda bekliyor değil, onay bekleyenler yaz".
        baslik: "Onay bekleyenler",
        tone: "onayda",
        sekme: "onay",
        bosMetin: "Onayınızı bekleyen iş yok.",
        // En UZUN bekleyen üstte (ilk teslim edilen ilk sırada).
        params: { scope, status: "onay_bekliyor", sort: "bekleyen" },
      },
      yakinda: {
        key: "yakinda",
        baslik: "Yakındakiler",
        tone: "yakinda",
        sekme: "yakinda",
        bosMetin: "Süresi yaklaşan iş yok.",
        // Süresi en yakın olan üstte.
        params: { scope, due_soon: 1, sort: "termin" },
      },
      yeni: {
        key: "yeni",
        baslik: "Son verdikleriniz",
        tone: "yeni",
        sekme: "yeni",
        sort: "yeni",
        bosMetin: "Bekleyen yeni iş yok.",
        // "Yeni" sekmesinin kümesi, en son verilen üstte.
        params: { scope, kume: "yeni", sort: "yeni" },
      },
    };
  };

  const telefon = useKatman() === "telefon";

  const yeniGorevCipi = (ekSinif = "") =>
    olusturabilir ? (
      <button
        type="button"
        className={`gk-toolchip gk-toolchip--birincil ${ekSinif}`.trim()}
        onClick={() => openNewChat("orta")}
      >
        <PlusIcon className="gk-ic" /> Yeni Talep
      </button>
    ) : null;

  // FAZ-80 — MOBİLDE "YENİ TALEP" YAZILI ÇİP DEĞİL, YUVARLAK + DÜĞMESİ.
  //
  // Kullanıcı (2026-07-30): "mobilde 'Yeni Talep' gibi bir şey yazma… yeni
  // talebi yuvarlak hâle getirip artı şeklinde, büyükçe, aynı sohbet ikonunun
  // şeklinde/o büyüklükte bir artı butona koyup SOHBET BUTONUNUN ÜSTÜNE
  // ekleyebiliriz — sadece bu sayfada olmak üzere."
  //
  // Sorun buydu: yazılı çip (`gk-mobil-yeni`, right:14 bottom:76) sohbet
  // balonuyla ÇAKIŞIYORDU — ikisi de sağ-alt köşede sabit. Artık ikisi aynı
  // sütunda ÜST ÜSTE dizili (konum gorev.css'te, balonun ölçüsünü chat.css
  // değişkenlerinden okur → biri değişince diğeri kaymaz).
  //
  // Kullanıcının önce düşündüğü "mobilde sohbet düğmesi yeni-talep işi yapsın"
  // fikri KENDİSİ tarafından geri alındı: "böyle olmaz, vazgeçtim — kişi bu
  // sayfadayken diğer sohbetlere bakmak isterse sorun yaşar." Bu yüzden sohbet
  // düğmesi sohbet olarak kaldı; + ayrı bir düğme.
  const mobilYeniTalepFab = () =>
    olusturabilir ? (
      <button
        type="button"
        className="gk-yeni-fab"
        onClick={() => openNewChat("orta")}
        aria-label="Yeni talep"
        title="Yeni talep"
      >
        <PlusIcon />
      </button>
    ) : null;

  // Satır-içi aksiyon: "Ne durumdayız?"
  //
  // KARARI BACKEND VERİR (kullanıcı şartı 2026-07-28: "front end tabanlı yapma,
  // backend yap"). Satır artık nesne-düzeyi kuralı KOPYALAMIYOR; serializer'ın
  // `viewer.can_ask_status` bayrağını okuyor. Bayrak arkasında üç koşul var:
  //   • tasks.ask_status izni
  //   • görev GÖREVLİDE (yeni / devam_ediyor / geri_bildirim_bekliyor) —
  //     onaya gidip düzeltmeyle geri dönen iş de buraya düşer
  //   • bakan kişi MAKAM tarafında (veren) — işi YAPAN taraftaysan çıkmaz
  //
  // Son madde bu turun asıl düzeltmesi: başkan yardımcısı `tasks.view_all`
  // taşıdığı için başkanın KENDİSİNE verdiği işte de "Ne durumdayız?" görüyor
  // ve bastığında soru kendisine düşüyordu. Artık backend `_is_makam` işi yapan
  // tarafı makam saymıyor; buton da API de kapalı.
  const kartAksiyonlari = (task) => {
    if (!task?.viewer?.can_ask_status) return null;
    return (
      <SoruPopover
        label="Ne durumdayız?"
        sorular={PRESET_QUESTIONS}
        onSec={(soru) => soruGonder(task, soru)}
        trigger={
          <span className="gk-satirchip gk-satirchip--sor">
            <ChatBubbleOvalLeftEllipsisIcon className="gk-ic" /> Ne durumdayız?
          </span>
        }
      />
    );
  };

  const bosMetin = (sekme) => {
    if (!kapsam) {
      return "Talep verebiliyorsunuz ama verdiğiniz talepleri görüntüleme yetkiniz yok.";
    }
    if (sekme === "tamamlanan") return "Bu aralıkta kapanmış iş yok.";
    if (sekme === "onay") return "Onayınızı bekleyen iş yok.";
    if (sekme === "geciken") return "Geciken iş yok — hepsi zamanında gidiyor.";
    if (sekme === "yakinda") return "Teslim tarihi yaklaşan iş yok.";
    return olusturabilir
      ? "Şu an açık talebiniz yok — yukarıdan ilk talebi verebilirsiniz."
      : "Şu an açık talebiniz yok.";
  };

  return (
    <>
      <GorevListeIskeleti
        title="Talep Gönder"
        accent="ata"
        scope={kapsam}
        infoTitle="Talep Gönder"
        infoContent={BILGI}
        infoWide
        searchPlaceholder="Talep konusu, kişi ya da birim adı…"
        renderFilters={filtreler}
        /* FAZ-90: telefonda yuvarlak + düğmesi ARAÇ SATIRINDA çizilir.
           Eskiden sağ-altta yüzüyordu ve alt barın merkez düğmesiyle
           fiziksel olarak çakışıyordu. Masaüstü/tablette yazılı çip aynen. */
        renderHeaderActions={() =>
          telefon ? mobilYeniTalepFab() : yeniGorevCipi("gk-yeni-gorev")
        }
        renderCardActions={kartAksiyonlari}
        railKartlari={seritKartlari}
        tarafGoster="atanan"
        bosMetin={bosMetin}
        tazeleRef={tazeleRef}
      />

      {/* FAZ-90: yüzen konum kaldırıldı — düğme artık araç satırında
          (renderHeaderActions). Alt bar sağ-alt köşeyi devraldı. */}
    </>
  );
}
